import React, {useContext, useEffect, useState} from 'react';
import {ThemeProvider} from "@mui/material/styles";
import {AppContext} from "../utils/appContext";
import { ThemeContext } from '../utils/themeContext';
import {createTheme, darken} from "@mui/material";

const Theme = (props: { children: React.ReactNode; }) => {

	const appContext = useContext(AppContext);
	const [settings, setSettings] = useState<IBuildOptionsTheme | null>(null)
	const [theme, setTheme] = useState<any>(null)

	useEffect( () => {
		switch (appContext.website) {
			case 'HU':
			case 'AM':
			case 'NG':
			case 'CF':
			case 'PA':
			case 'RO':
			case 'FA':
			case 'IP':
			case 'MS':
			case 'JO':
			case 'BK':
				setSettings({
					primary: '#94D82D',
					secondary: '#FFD43B',
					mainFont: "Circular Spotify Tx T Book",
					secondaryFont: "Circular Spotify Tx T Book"
				});
				break;
			case 'CE':
				setSettings({
					primary: '#619B8A',
					secondary: '#FCCA46',
					mainFont: "Rubik, sans-serif",
					secondaryFont: "Rubik, sans-serif"
				});
				break;
			case 'LD':
			case 'VN':
			case 'MU':
				setSettings({
					primary: '#7E89FD',
					secondary: '#F07E6E',
					mainFont: 'Maven Pro',
					secondaryFont: 'Roboto'
				});
				break;
			case 'VP':
				setSettings({
					primary: '#DCB06C',
					secondary: '#132646',
					mainFont: 'Playfair Display',
					secondaryFont: 'Roboto',
				});
				break;
			case 'LC':
				setSettings({
					primary: '#93C598',
					secondary: '#51354F',
					mainFont: 'Playfair Display',
					secondaryFont: 'Roboto'
				});
				break;
			case 'EV':
				setSettings({
					primary: '#e50051',
					secondary: '#e50051',
					mainFont: 'Sofia Pro, sans-serif',
					secondaryFont: 'Synthese, sans-serif'
				});
				break;
		}
	}, [appContext.website]);

	useEffect( ()=> {
		if (settings) {
			setTheme(buildOptions(settings));
		}
	}, [settings])


  return (
    settings &&
    theme && (
      <ThemeContext.Provider value={theme}>
        <ThemeProvider theme={theme}>
          {props.children}
        </ThemeProvider>
      </ThemeContext.Provider>
    )
  )
};

export default Theme;

interface IBuildOptionsTheme{
	primary: string,
	secondary: string,
	mainFont: string,
	secondaryFont: string
}

const buildOptions = ({primary, secondary, mainFont, secondaryFont}: IBuildOptionsTheme) => {
	return createTheme({
		palette: {
			primary: {
				main: primary,
			},
			secondary: {
				main: secondary
			}
		},
		typography: {
			htmlFontSize: 16,
			h1: {
				fontSize: 40,
				fontFamily: mainFont,
				fontWeight: 'bold',
				lineHeight: '45px'
			},
			h2: {
				fontSize: 35,
				fontFamily: mainFont,
				fontWeight: 'bold',
				lineHeight: '35px'
			},
			h3: {
				fontSize: 30,
				fontFamily: mainFont,
				fontWeight: 'bold',
				lineHeight: '35px'
			},
			h4: {
				fontSize: 26,
				fontFamily: mainFont,
				fontWeight: 'bold',
				lineHeight: '30px'
			},
			h5: {
				fontSize: 20,
				fontFamily: mainFont,
				fontWeight: 'bold',
				lineHeight: '26px'
			},
			h6: {
				fontSize: 16,
				fontFamily: mainFont,
				fontWeight: 500,
				lineHeight: '26px',
			},
			subtitle1: {
				fontSize: 16,
				fontFamily: mainFont,
				lineHeight: '15px',
			},
			subtitle2: {
				fontSize: 12,
				fontFamily: mainFont,
				fontWeight: 'bold',
				lineHeight: '15px',
				color: primary,
				textTransform: 'uppercase',
			},
			body1: {
				fontSize: 16,
				fontFamily: secondaryFont,
				lineHeight: '19px',
				fontWeight: 400,
			},
			body2: {
				fontSize: 14,
				fontFamily: secondaryFont,
				lineHeight: '16px',
				fontWeight: 300,
			},
			caption: {
				fontSize: 18,
				fontFamily: mainFont,
				fontWeight: 500,
			},
			overline: {
				fontFamily: mainFont,
				fontSize: 14,
			}
		},
		mixins: {
			toolbar: {
				minHeight: '100px',
			}
		},
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '@global': {
            'body': {
              margin: 0,
            },
            'ul': {
              listStyle: 'none',
              padding: '0'
            },
            'a': {
              textDecoration: 'none',
              color: 'inherit',
            },
            'img': {
              maxWidth: '100%',
              display: 'block',
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            border: '1px solid #e5e5e5',
            "&:hover $notchedOutline": {
              borderColor: '#000',
            },
            "&$focused $notchedOutline": {
              // border: '0px solid blue'
            },
            borderRadius: 0,
          },
          notchedOutline: {
            borderRadius: 0,
            border: '0'
          },
        }
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            borderRadius: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            '&:hover': {
              backgroundColor: '#FFFFFF',
            },
            '&.Mui-focused': {
              borderColor: '#2357D0',
              backgroundColor: '#FFFFFF',
            },
            '&::before': {
              borderBottom: '0!important',
            },
            '&::after': {
              borderBottom: '0!important',
            }
          },
          input: {
            padding: '12px 12px 10px!important'
          }
        }
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: 9,
            margin: 0,
            marginTop: 3,
            marginLeft: '5px!important',
            marginRight: '0!important'
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            padding: 8,
            '&.Mui-selected': {
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            height: 48,
            background: '#FFFFFF',
            borderRadius: 5,
            borderColor: '#333333',
            color: '#333333'
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          'outlined': {
            transform: "translate(14px, 18px) scale(1)"
          },
          root: {
            transform: 'none!important',
            '&.Mui-focused': {
              color: '#2357D0!important'
            }
          }
        }
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            backgroundColor: '#FFFFFF',
            borderRadius: 5,
            width: '100%'
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            height: 48,
          },
          formControl: {
            borderRadius: '0!important'
          },
          input: {
            borderColor: '#e5e5e5!important'
          }
        }
      },
      MuiInput: {
        styleOverrides: {
          underline: {
            '&:hover': {
              '&:before': {
                borderBottom: '1px solid #cbcbcb!important'
              }
            },
            borderBottom: '0!important',
            '&:before': {
              borderBottom: '1px solid #cbcbcb!important'
            },
            '&:after': {
              borderBottom: '1px solid #cbcbcb!important'
            }
          }
        }
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            fontSize: 24,
            color: primary,
            '& svg#calendar-dots_svg__Layer_1': {
              color: '#000!important'
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            fontSize: 24,
            color: '#000',
          },
          iconOutlined: {
            // right: 25
          },
          iconOpen: {
            transform: 'rotate(0deg)'
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            fontSize: 24,
            color: 'inherit'
          },
        }
      },
      MuiButton: {
        styleOverrides: {
          text: {
            padding: 0,
            textTransform: 'initial',
            fontFamily: mainFont,
            '&[type=reset]': {
              color: 'red'
            }
          },
          outlined: {
            height: 48,
            borderRadius: 5,
          },
          contained: {
            height: 48,
            boxShadow: '0px 3px 6px #00000029!important',
            borderRadius: 5,
            fontFamily: mainFont,
          },
          containedPrimary: {
            '& $label': {
              fontWeight: 'bold',
              textTransform: 'uppercase',
              fontFamily: mainFont
            }
          },
          containedSecondary: {
            '& $label': {
              color: '#FFFFFF',
              fontWeight: 'bold',
              textTransform: 'uppercase',
            },
            '&:hover': {
              backgroundColor: darken(secondary, 0.2),
            }
          },
        }
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: 0,
          }
        }
      },
      MuiPopover: {
        styleOverrides: {
          root: {
            marginTop: 5,
            '& .MuiPaper-root': {
              overflow: 'visible!important',
              border: '1px solid black',
              '& .MuiBox-root': {
                minWidth: 192
              },
              '& .MuiBadge-root': {
                content: "''",
                position: "absolute",
                top: -30,
                left: 10,
                borderBottom: '15px solid white',
                borderRight: '15px solid transparent',
                borderLeft: '15px solid transparent',
                borderTop: '15px solid transparent',
                zIndex: 10,
              }
            }
          },
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: '0',
            boxShadow: '0px 3px 6px #00000029!important',
          }
        }
      },
      MuiCardActionArea: {
        styleOverrides: {
          root: {
            '&:focus': {
              outline: 'none!important'
            },
            '&:hover': {
              backgroundColor: 'transparent!important'
            },
            borderBottom: '4px solid transparent',
            margin: 'auto'
          },
          focusHighlight: {
            opacity: '0!important'
          }
        }
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            '& p': {
              fontSize: '0.875rem!important',
              fontFamily: "Roboto, sans-serif",
            },
            padding: '0 8px!important'
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily: mainFont
          }
        }
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            alignItems: 'baseline',
            '& i': {
              color: primary,
              marginRight: 9
            }
          },
          dense: {
            '& span': {
              fontSize: 14,
            },
            '& i': {
              color: 'initial',
            },
          },
          gutters: {
            paddingRight: 0,
            paddingLeft: 0,
          }
        }
      },
      MuiStepper: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            width: 'fit-content',
            margin: 'auto',
            padding: 10,
          }
        }
      },
      MuiStep: {
        styleOverrides: {
          root: {
            maxWidth: 150,
          }
        }
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: '#DEDEDE',
            '&.Mui-text': {
              fontSize: 12,
              color: primary,
              fill: 'rgba(0, 0, 0, 0.87)'
            },
            '&.Mui-active': {
              color: '#FFFFFF!important',
              borderRadius: '50%',
              borderColor: secondary,
              border: 1,
              borderStyle: 'solid'
            },
          }
        }
      },
      MuiStepLabel: {
        styleOverrides: {
          root: {
            color: primary,
            '&.Mui-label': {
              color: '#DEDEDE',
              fontWeight: 'bold'
            },
            '&.Mui-active': {
              color: '#000000',
              fontWeight: 'bold'
            },
            '&.Mui-completed': {
              color: '#535353',
              fontWeight: 'bold'
            },
          }
        }
      },
      MuiSlider: {
        styleOverrides: {
          markLabel: {
            top: 20,

          },
        }
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            padding: 5
          },
          colorPrimary: {
            color: '#000!important'
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            boxShadow: '0px 3px 6px #00000029!important',
            color: 'white',
            fontSize: 12,
            fontWeight: 'bold'
          },
          colorSecondary: {
            color: 'white',
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          popper: {
            zIndex: 999999
          }
        }
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            fontSize: 14
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            width: '100%',
            border: 0,
            backgroundColor: 'transparent',
            zIndex: 0,
            position: 'inherit'
          },
          paperAnchorDockedLeft: {
            borderRight: 0
          }
        }
      },
      MuiRadio: {
        styleOverrides: {
          colorSecondary: {
            color: 'black!important'
          }
        }
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: '#000',
          }
        }
      }
    }
	})
};
